'use client';

import { DEFAULT_SORT } from '@ria-client/advisor-search/constants';
import { countActiveFilters } from '@ria-client/advisor-search/query';
import {
  ADVISOR_SEARCH_PAGE_SIZE,
  type SearchParams,
  type SearchResponse,
} from '@ria-client/advisor-search/types';
import { useMemo, useRef, useState } from 'react';

import { FiltersDialog, type FilterValues } from '../FiltersDialog/FiltersDialog';
import { ResultsContent } from './ResultsContent';
import { ResultsToolbar } from './ResultsToolbar';
import { useResultsState } from './useResultsState';

export interface ResultsProps {
  initialParams: SearchParams;
  initialData: SearchResponse | null;
  initialError: string | null;
}

export function Results({ initialParams, initialData, initialError }: ResultsProps) {
  const { params, data, error, isPending, handlers } = useResultsState({
    initialParams,
    initialData,
    initialError,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersButtonRef = useRef<HTMLButtonElement>(null);
  const currentPage = params.page ?? 1;
  const startPosition = (currentPage - 1) * ADVISOR_SEARCH_PAGE_SIZE + 1;
  const activeFilterCount = countActiveFilters(params);
  const isFetching = data === null && error === null;
  const isLoading = isPending || isFetching;

  const filtersInitialValues = useMemo<FilterValues>(
    () => ({
      services: params.services ?? [],
      fees: params.fees ?? [],
      clientTypes: params.clientTypes ?? [],
      experience: params.experience ?? [],
    }),
    [params.services, params.fees, params.clientTypes, params.experience],
  );

  return (
    <>
      <ResultsToolbar
        filterButtonRef={filtersButtonRef}
        activeFilterCount={activeFilterCount}
        onOpenFilters={() => setFiltersOpen(true)}
        sort={params.sort ?? DEFAULT_SORT}
        onSortChange={handlers.onSortChange}
        isLoading={isLoading}
      />
      <section aria-label="Disclosure" className="bg-muted px-4 py-3 sm:px-6">
        <p className="typography-text-small mx-auto max-w-[1248px] text-muted-foreground">
          <strong>Disclosure:</strong> The data for NerdWallet’s Investment Advisor search tool is
          sourced from the U.S. Securities Exchange Commission Investment Adviser Public Disclosure
          (IAPD) database. NerdWallet does not create or control this data. We make no
          representations regarding its accuracy, completeness, reliability, or timeliness.
          Information may be incomplete, outdated, or subject to error. Results may include
          references or links to external websites or materials. We do not endorse and are not
          responsible for the content, accuracy, or practices of any third-party resources.
        </p>
      </section>
      <div className="mx-auto mb-8 max-w-[1248px] px-4 sm:px-0">
        <ResultsContent
          data={data}
          isPending={isLoading}
          error={error}
          retry={handlers.onRetry}
          zip={params.q}
          activeFilterCount={activeFilterCount}
          currentPage={currentPage}
          startPosition={startPosition}
          onPageChange={handlers.onPageChange}
        />
      </div>
      <FiltersDialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        initialValues={filtersInitialValues}
        onApply={handlers.onApply}
        isLoading={isLoading}
        triggerRef={filtersButtonRef}
      />
    </>
  );
}
